import type { CampaignSelectDto } from '@@types/api.types';
import { OPERATIONAL_REGION_LABELS } from '@azymmetric/shared/types/meia/backend-persistence.types';
import { LEVEL_OF_WARFARE_LABELS } from '@components/pages/campaigns/components/campaign-modal.shared';
import { BadgeList } from '@components/shared/badge-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { EditIcon } from 'lucide-react';
import type { FC } from 'react';

type CampaignScenariosTileProps = {
  campaign: CampaignSelectDto;
  onEdit: () => void;
};

export const CampaignScenariosTile: FC<CampaignScenariosTileProps> = (props) => {
  const { campaign, onEdit } = props;
  const scenarios = campaign.scenarios;
  const isEmpty = scenarios.length === 0;

  return (
    <Tile
      emptyState={{
        message: '+ Add Scenario',
        onClick: onEdit,
        title: 'No Scenarios',
      }}
      header={{
        iconButton: { ariaLabel: 'Edit scenarios', icon: <EditIcon />, onClick: onEdit },
        title: 'Scenarios',
      }}
      hoverReveal
      isEmpty={isEmpty}
    >
      <div className="flex flex-col gap-3">
        {scenarios.map((scenario) => (
          <div
            className="flex flex-col gap-3 rounded-md border border-gray-200 p-3"
            key={scenario.id}
          >
            <div className="flex flex-col gap-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-800">{scenario.name}</span>
                <ClassificationBadge classification={scenario.classificationLevel} />
                <Badge
                  size="xs"
                  variant="secondary"
                >
                  {LEVEL_OF_WARFARE_LABELS[scenario.levelOfWarfare]}
                </Badge>
              </div>
              <BlueLabel text="Scenario" />
            </div>
            {scenario.description && (
              <p className="whitespace-pre-wrap text-sm text-gray-700">{scenario.description}</p>
            )}
            {scenario.operationalRegions.length > 0 && (
              <BadgeList
                badgeVariant="outline"
                items={scenario.operationalRegions.map((region) => ({
                  id: region,
                  name: OPERATIONAL_REGION_LABELS[region],
                }))}
                limit={2}
                size="xs"
              />
            )}

            {scenario.vignettes.length > 0 && (
              <div className="flex flex-col gap-2">
                {scenario.vignettes.map((vignette) => (
                  <div
                    className="rounded-md border border-gray-200 p-2"
                    key={vignette.id}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{vignette.name}</span>
                    </div>
                    <BlueLabel
                      className="mt-0.5"
                      text="Vignette"
                    />
                    {vignette.description && (
                      <p className="mt-1 whitespace-pre-wrap text-xs text-gray-600">{vignette.description}</p>
                    )}
                    {vignette.missions.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {vignette.missions.map((mission) => (
                          <div
                            className="flex flex-col gap-1 rounded-md border border-gray-200 px-3 py-2"
                            key={mission.id}
                          >
                            <span className="text-sm font-medium text-gray-800">{mission.name}</span>
                            <BlueLabel
                              className="text-xs"
                              text="Mission"
                            />
                            <span className="text-xs text-gray-500">{mission.studyName ?? mission.studyId}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Tile>
  );
};
